import React, { useEffect, useRef } from 'react';
import {
  ArticleForm,
  EventForm,
  AlbumForm,
  CanticleForm,
  BookForm,
} from '../';
import { apiService } from '../../services';
import {
  Article,
  ArticleCreate,
  Event,
  EventCreate,
  Album,
  AlbumCreate,
  Book,
  BookCreate,
  Canticle,
  CanticleCreate,
} from '../../types';

type SelectedType = 'articles' | 'events' | 'albums' | 'canticles' | 'books';

interface CreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: SelectedType;
  loading?: boolean;
  error?: string | null;
  onSubmit: (
    type: SelectedType,
    data: ArticleCreate | EventCreate | AlbumCreate | BookCreate | CanticleCreate,
    files?: File | FileList
  ) => Promise<Article | Event | Album | Book | Canticle | void>;
}

const getTypeLabel = (type: SelectedType): string => {
  const labels: Record<SelectedType, string> = {
    articles: 'Article',
    events: 'Événement',
    albums: 'Album',
    canticles: 'Cantique',
    books: 'Édition',
  };
  return labels[type];
};

export const CreateModal: React.FC<CreateModalProps> = ({
  isOpen,
  onClose,
  type,
  loading = false,
  error = null,
  onSubmit,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && e.target === modalRef.current) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const commonProps = {
    loading,
    error,
    onCancel: onClose,
  };

  const renderForm = () => {
    switch (type) {
      case 'articles':
        return (
          <ArticleForm
            onSubmit={async (data, imageFile) => {
              // Call the handler which creates the article and returns it
              const article = await onSubmit('articles', data, imageFile) as Article;
              return article;
            }}
            onFileUpload={async (articleId, files) => {
              for (const file of files) {
                await apiService.uploadArticleFile(articleId, file);
              }
            }}
            {...commonProps}
          />
        );
      case 'events':
        return (
          <EventForm
            onSubmit={async (data, imageFile) => {
              await onSubmit('events', data, imageFile);
              onClose();
            }}
            {...commonProps}
          />
        );
      case 'albums':
        return (
          <AlbumForm
            onSubmit={async (data) => {
              await onSubmit('albums', data, (data as any).files);
              onClose();
            }}
            {...commonProps}
          />
        );
      case 'canticles':
        return (
          <CanticleForm
            onSubmit={async (data, pdfFile) => {
              await onSubmit('canticles', data, pdfFile);
              onClose();
            }}
            {...commonProps}
          />
        );
      case 'books':
        return (
          <BookForm
            onSubmit={async (data, coverFile) => {
              await onSubmit('books', data, coverFile);
              onClose();
            }}
            {...commonProps}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      ref={modalRef}
      className='fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm'
      role='dialog'
      aria-modal='true'
      aria-labelledby='create-modal-title'
    >
      <div className='relative w-full max-w-2xl bg-white rounded-lg shadow-xl p-6 m-4 max-h-[90vh] overflow-y-auto'>
        <button
          onClick={onClose}
          className='absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded'
          aria-label='Fermer'
        >
          <svg
            className='w-6 h-6'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            aria-hidden='true'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        </button>

        <h2
          id='create-modal-title'
          className='text-2xl font-bold text-gray-900 mb-6'
        >
          Créer un {getTypeLabel(type)}
        </h2>

        {renderForm()}
      </div>
    </div>
  );
};
